import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Printer, ArrowRight } from "lucide-react";

const ORDER_DETAILS_ENDPOINT = (orderId, txnid) =>
  `https://tyka.premierhostings.com/backend/api/orders/${orderId}?txnid=${encodeURIComponent(
    txnid || ""
  )}`;

const INR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const txnid = params.get("txnid");

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  const order = useMemo(() => {
    if (!data) return null;
    const o = data;
    const address = o.user_address || {};

    // 🛒 Map products from order_details.data
    const items = (o.order_details?.data || []).map((it) => ({
      name: it.products?.name || "Unknown Product",
      qty: it.quantity || 0,
      price: parseFloat(it.price || 0),
    }));

    const subtotal = items.reduce((sum, it) => sum + it.price * it.qty, 0);
    const shipping = 0; // Backend se shipping aaye to replace kare
    const grandTotal = subtotal + shipping; // ✅ FIXED: Dynamically calculate grand total

    const mappedOrder = {
      id: o.id,
      number: o.order_code || `#${o.id}`,
      createdAt: o.order_date || o.created_at,
      payment: {
        method: o.payment_type || "PayU",
        txnid: o.transaction_id || txnid,
        amount: grandTotal, // ✅ FIXED: use calculated grand total
        status: o.payment_status || "pending",
      },
      customer: {
        name: address.name || "Customer",
        phone: address.phone_number || "",
      },
      shipping: {
        line1: address.street || "",
        line2: address.apartment_suite || "",
        city: address.city?.name || "",
        state: address.state?.name || "",
        pincode: address.zipcode || "",
        country: address.country?.name || "India",
      },
      items,
      totals: {
        subtotal,
        shipping,
        grand_total: grandTotal, // ✅ FIXED
      },
    };

    console.log("🟢 Order Mapped:", mappedOrder);
    return mappedOrder;
  }, [data, txnid]);

  useEffect(() => {
    if (!orderId || !txnid) {
      setErr("Missing order_id or txnid in URL.");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setErr("");
        const url = ORDER_DETAILS_ENDPOINT(orderId, txnid);
        const res = await axios.get(url);

        console.log("📦 API Response:", res.data.data);
        setData(res.data.data);
      } catch (e) {
        console.error("❌ API Fetch Error:", e);
        setErr("Could not fetch live order details.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orderId, txnid]);

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <p className="text-gray-600">Loading receipt…</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-red-600 font-medium">Unable to render receipt.</p>
      </div>
    );
  }

  const { number, createdAt, payment, customer, shipping, items, totals } =
    order;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 print:p-0">
      {/* Success Header */}
      <div className="flex items-center justify-center mb-6">
        <CheckCircle className="w-6 h-6 mr-2 text-green-600" />
        <h1 className="text-xl font-semibold">Payment successful</h1>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-center gap-3 mb-6 no-print">
        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-gray-50"
        >
          <Printer className="w-4 h-4" /> Print / Save PDF
        </button>
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-lg bg-black text-white px-4 py-2 text-sm font-medium hover:opacity-90"
        >
          Continue Shopping <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Receipt */}
      <div className="bg-white rounded-2xl shadow border print:shadow-none print:border-0">
        <div className="p-6 border-b">
          <div className="flex flex-col md:flex-row md:justify-between">
            <div>
              <h2 className="text-lg font-semibold">TYKA Sports</h2>
              <p className="text-sm text-gray-600">Thank you for your order</p>
            </div>
            <div className="text-sm text-gray-700 mt-2 md:mt-0">
              <p>
                <span className="text-gray-500 mr-2">Order:</span>
                {number}
              </p>
              <p>
                <span className="text-gray-500 mr-2">Date:</span>
                {new Date(createdAt).toLocaleString()}
              </p>
              <p>
                <span className="text-gray-500 mr-2">TxnID:</span>
                {payment.txnid}
              </p>
              <p>
                <span className="text-gray-500 mr-2">Method:</span>
                {payment.method}
              </p>
              <p>
                <span className="text-gray-500 mr-2">Status:</span>
                {payment.status}
              </p>
            </div>
          </div>
        </div>

        {/* Customer + Shipping */}
        <div className="p-6 border-b grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border p-4">
            <h3 className="text-sm font-semibold mb-2">Customer</h3>
            <p className="text-sm">{customer.name}</p>
            <p className="text-sm">{customer.phone}</p>
          </div>
          <div className="rounded-lg border p-4">
            <h3 className="text-sm font-semibold mb-2">Shipping Address</h3>
            <p className="text-sm">
              {[shipping.line1, shipping.line2].filter(Boolean).join(", ")}
            </p>
            <p className="text-sm">
              {[shipping.city, shipping.state, shipping.pincode]
                .filter(Boolean)
                .join(", ")}
            </p>
            <p className="text-sm">{shipping.country}</p>
          </div>
        </div>

        {/* Products */}
        <div className="p-6 border-b">
          <h3 className="text-sm font-semibold mb-4">Products</h3>
          {items.length > 0 ? (
            <table className="w-full text-sm border">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2 border">Product</th>
                  <th className="text-center p-2 border">Qty</th>
                  <th className="text-right p-2 border">Price</th>
                  <th className="text-right p-2 border">Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx} className="border-t">
                    <td className="p-2">{it.name}</td>
                    <td className="p-2 text-center">{it.qty}</td>
                    <td className="p-2 text-right">{INR.format(it.price)}</td>
                    <td className="p-2 text-right">
                      {INR.format(it.qty * it.price)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-gray-500">No products found</p>
          )}
        </div>

        {/* Totals */}
        <div className="p-6">
          <div className="flex justify-between py-1 text-sm">
            <span>Subtotal</span>
            <span>{INR.format(totals.subtotal)}</span>
          </div>
          <div className="flex justify-between py-1 text-sm">
            <span>Shipping</span>
            <span>{INR.format(totals.shipping)}</span>
          </div>
          <div className="flex justify-between py-2 text-base font-semibold">
            <span>Grand Total</span>
            <span>{INR.format(totals.grand_total)}</span>
          </div>
        </div>
      </div>

      {err ? (
        <div className="mt-4 p-3 rounded-lg bg-yellow-50 text-yellow-800 text-sm">
          {err}
        </div>
      ) : null}
    </div>
  );
}
